/**
 * BuildActivityView - Kanban-style build view + activity log
 * 
 * Features:
 * - Visual Kanban board with column backgrounds and hints
 * - To Do builds: Click opens NewBuildModal in edit mode
 * - In Progress/Done builds: Click opens comprehensive summary modal
 * - Status change functionality (move between columns)
 * - Dismiss completed builds
 * - Activity log of all build events
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Loader2, Play, CheckCircle, Clock, X, AlertCircle,
  Activity, Lock, Unlock, Hammer, GitCommit, MessageSquare,
  AlertTriangle, RefreshCw, Server, User, Settings2, Globe,
  FileCode, ArrowRight, Eye, Edit3, ChevronRight, Archive
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { infrastructureApi, deliveryConfigApi } from '../../services/api';
import { formatDistanceToNow } from 'date-fns';
import NewBuildModal from './NewBuildModal';

// Column configuration
const COLUMNS = [
  {
    id: 'todo',
    label: 'To Do',
    color: 'bg-blue-400',
    bgColor: 'bg-gradient-to-b from-blue-50 to-blue-100/50',
    borderColor: 'border-blue-200',
    hint: 'Build will move to In Progress when a developer picks it up in the AI environment',
    statuses: ['not_started', 'failed']
  },
  {
    id: 'in_progress',
    label: 'In Progress',
    color: 'bg-yellow-400',
    bgColor: 'bg-gradient-to-b from-yellow-50 to-yellow-100/50',
    borderColor: 'border-yellow-200',
    hint: 'Build is being executed by an AI assistant. View progress in real-time.',
    statuses: ['in_progress']
  },
  {
    id: 'done',
    label: 'Done',
    color: 'bg-green-400',
    bgColor: 'bg-gradient-to-b from-green-50 to-green-100/50',
    borderColor: 'border-green-200',
    hint: 'Build is complete. Infrastructure has been provisioned or updated.',
    statuses: ['complete', 'cancelled']
  }
];

const BuildActivityView = ({ productId, infraStatus, isOwner, onRefresh, currentVersion = 0 }) => {
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [changingStatus, setChangingStatus] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);  // NewBuildModal for To Do builds
  const [showSummaryModal, setShowSummaryModal] = useState(false);  // Summary modal for In Progress/Done
  const [buildConfig, setBuildConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [keyHolders, setKeyHolders] = useState([]);
  
  // Status change state
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Load activity log
  const loadActivities = useCallback(async () => {
    try {
      setLoadingActivities(true);
      const response = await infrastructureApi.getActivity(productId, 50);
      setActivities(response.data?.activities || []);
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setLoadingActivities(false);
    }
  }, [productId]);

  useEffect(() => {
    if (productId) {
      loadActivities();
    }
  }, [productId, loadActivities]);

  // Load build config and key holders for summary modal
  const loadBuildDetails = useCallback(async () => {
    try {
      setLoadingConfig(true);
      const [configRes, holdersRes] = await Promise.all([
        deliveryConfigApi.getConfig(productId),
        infrastructureApi.getMcpKeyHolders(productId).catch(() => ({ data: { key_holders: [] } }))
      ]);
      const config = configRes.data?.delivery_config || configRes.data;
      setBuildConfig(config);
      setKeyHolders(holdersRes.data?.key_holders || []);
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setLoadingConfig(false);
    }
  }, [productId]);

  // Handle card click - different behavior based on status
  const handleCardClick = () => {
    const status = infraStatus?.status;
    
    if (status === 'not_started' || status === 'failed') {
      // To Do builds: Open NewBuildModal in edit mode
      setShowEditModal(true);
    } else {
      // In Progress/Done builds: Open comprehensive summary modal
      loadBuildDetails();
      setSelectedAssignee(infraStatus?.task?.assignee_id || 'all');
      setSelectedStatus(status);
      setShowSummaryModal(true);
    }
  };

  // Handle edit modal close
  const handleEditModalClose = (result) => {
    setShowEditModal(false);
    if (result?.success) {
      onRefresh?.();
      loadActivities();
    }
  };

  // Handle status change (move between columns)
  const handleStatusChange = async (newStatus) => {
    try {
      setChangingStatus(true);
      await infrastructureApi.changeBuildStatus(productId, {
        new_status: newStatus,
        assignee_id: selectedAssignee !== infraStatus?.task?.assignee_id ? selectedAssignee : undefined
      });
      
      const statusLabels = {
        'not_started': 'To Do',
        'in_progress': 'In Progress',
        'complete': 'Done'
      };
      
      toast.success(`Build moved to ${statusLabels[newStatus]}`);
      setShowSummaryModal(false);
      onRefresh?.();
      loadActivities();
    } catch (error) {
      console.error('Failed to change status:', error);
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setChangingStatus(false);
    }
  };

  // Handle assignee change
  const handleAssigneeChange = async () => {
    try {
      setChangingStatus(true);
      await infrastructureApi.changeBuildStatus(productId, {
        new_status: infraStatus?.status,
        assignee_id: selectedAssignee
      });
      toast.success('Assignee updated');
      onRefresh?.();
      loadActivities();
    } catch (error) {
      console.error('Failed to change assignee:', error);
      toast.error(error.response?.data?.detail || 'Failed to change assignee');
    } finally {
      setChangingStatus(false);
    }
  };

  // Handle dismiss (for completed builds)
  const handleDismiss = async () => {
    try {
      setDismissing(true);
      await infrastructureApi.dismissBuild(productId);
      toast.success('Build dismissed from board');
      setShowSummaryModal(false);
      onRefresh?.();
      loadActivities();
    } catch (error) {
      console.error('Failed to dismiss build:', error);
      toast.error(error.response?.data?.detail || 'Failed to dismiss build');
    } finally {
      setDismissing(false);
    }
  };

  // Get status config
  const getStatusConfig = (status) => {
    switch (status) {
      case 'not_started':
        return { 
          color: 'bg-blue-100 text-blue-700 border-blue-200', 
          icon: Clock,
          label: 'Waiting to Start',
          column: 'todo'
        };
      case 'in_progress':
        return { 
          color: 'bg-yellow-100 text-yellow-700 border-yellow-200', 
          icon: Play,
          label: 'In Progress',
          column: 'in_progress'
        };
      case 'complete':
        return { 
          color: 'bg-green-100 text-green-700 border-green-200', 
          icon: CheckCircle,
          label: 'Complete',
          column: 'done'
        };
      case 'failed':
        return { 
          color: 'bg-red-100 text-red-700 border-red-200', 
          icon: AlertCircle,
          label: 'Failed',
          column: 'todo'
        };
      case 'cancelled':
        return { 
          color: 'bg-gray-100 text-gray-700 border-gray-200', 
          icon: X,
          label: 'Cancelled',
          column: 'done'
        };
      default:
        return { 
          color: 'bg-gray-100 text-gray-600 border-gray-200', 
          icon: Clock,
          label: 'Unknown',
          column: 'todo'
        };
    }
  };

  // Get activity icon
  const getActivityIcon = (type) => {
    switch (type) {
      case 'build_created':
      case 'bootstrap_created':
        return <Hammer className="w-4 h-4 text-blue-500" />;
      case 'build_started':
      case 'bootstrap_started':
        return <Play className="w-4 h-4 text-yellow-500" />;
      case 'build_completed':
      case 'bootstrap_completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'build_failed':
      case 'bootstrap_failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'build_cancelled':
        return <X className="w-4 h-4 text-gray-500" />;
      case 'version_published':
      case 'constraints_published':
        return <GitCommit className="w-4 h-4 text-purple-500" />;
      case 'resource_created':
        return <Server className="w-4 h-4 text-blue-500" />;
      case 'decision':
        return <MessageSquare className="w-4 h-4 text-purple-500" />;
      case 'blocker':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  // Format value for display
  const formatValue = (value) => {
    if (Array.isArray(value)) {
      return value.map(v => v.replace(/_/g, ' ')).join(', ');
    }
    if (typeof value === 'string') {
      return value.replace(/_/g, ' ');
    }
    return String(value ?? 'Not set');
  };

  const task = infraStatus?.task;
  const hasActiveBuild = task && ['not_started', 'in_progress'].includes(infraStatus?.status);
  const statusConfig = getStatusConfig(infraStatus?.status);
  const isEditable = infraStatus?.status === 'not_started';

  return (
    <div className="space-y-6">
      {/* Kanban Board */}
      <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl p-4 border border-gray-200">
        <div className="grid grid-cols-3 gap-4">
          {COLUMNS.map((column) => {
            const isActiveColumn = column.statuses.includes(infraStatus?.status);
            
            return (
              <div key={column.id} className="space-y-3">
                {/* Column Header */}
                <div className="flex items-center gap-2 px-2">
                  <div className={cn("w-3 h-3 rounded-full", column.color)} />
                  <h3 className="font-semibold text-gray-800">{column.label}</h3>
                  {isActiveColumn && hasActiveBuild && (
                    <Badge variant="secondary" className="text-xs ml-auto">1</Badge>
                  )}
                </div>
                
                {/* Column Body */}
                <div className={cn(
                  "min-h-[280px] rounded-lg p-3 border-2 border-dashed transition-all",
                  column.bgColor,
                  column.borderColor,
                  isActiveColumn && hasActiveBuild && "border-solid"
                )}>
                  {/* Card or Hint */}
                  {isActiveColumn && hasActiveBuild ? (
                    <BuildCard 
                      task={task} 
                      infraStatus={infraStatus}
                      statusConfig={statusConfig}
                      onClick={handleCardClick}
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center p-4">
                      <p className="text-xs text-gray-400 text-center italic">
                        {column.hint}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* No Active Build Message */}
      {!hasActiveBuild && (
        <Card className="border-dashed border-2">
          <CardContent className="py-8 text-center">
            <Hammer className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <h3 className="text-base font-medium text-gray-700 mb-1">No Active Builds</h3>
            <p className="text-sm text-gray-500">
              Start a new build from the Current Architecture tab to see it here.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Activity Log */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              <CardTitle className="text-lg">Build Activity</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadActivities}
              disabled={loadingActivities}
            >
              <RefreshCw className={cn("w-4 h-4", loadingActivities && "animate-spin")} />
            </Button>
          </div>
          <CardDescription>
            History of architecture changes and builds
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {loadingActivities ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>No activity yet</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {activities.map((activity, index) => (
                <div
                  key={activity.activity_id || index}
                  className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="mt-0.5">
                    {getActivityIcon(activity.activity_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900">
                      {activity.message}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                      <span>{activity.user_name || 'System'}</span>
                      <span>•</span>
                      <span>
                        {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                  {activity.version && (
                    <Badge variant="outline" className="text-xs shrink-0">
                      v{activity.version}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* NewBuildModal for To Do builds - edit mode */}
      <NewBuildModal
        open={showEditModal}
        onClose={handleEditModalClose}
        productId={productId}
        isOwner={isOwner}
        currentVersion={currentVersion}
        isFirstBuild={currentVersion === 0}
        editBuild={infraStatus?.status === 'not_started' || infraStatus?.status === 'failed' ? {
          task: infraStatus?.task,
          status: infraStatus?.status,
          is_blocking: infraStatus?.is_blocking,
          draft_config: buildConfig  // This gets loaded when modal opens
        } : null}
      />

      {/* Summary Modal for In Progress/Done builds */}
      <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-500" />
              Build Details
              {infraStatus?.is_blocked && (
                <Badge variant="destructive" className="ml-2">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Blocked
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              View configuration and manage this build
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingConfig ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Blocked Alert */}
                {infraStatus?.is_blocked && infraStatus?.blocker_reason && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      <span className="font-medium">Blocked: </span>
                      {infraStatus.blocker_reason}
                      {infraStatus.blocked_by_name && (
                        <span className="text-xs block mt-1 text-red-600">
                          Blocked by {infraStatus.blocked_by_name} • {infraStatus.blocked_at && formatDistanceToNow(new Date(infraStatus.blocked_at), { addSuffix: true })}
                        </span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Build Info & Actions */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Hammer className="w-4 h-4 text-orange-500" />
                      Build Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div>
                        <span className="text-gray-500">Title:</span>{' '}
                        <span className="font-medium">{infraStatus?.task?.title || 'Untitled'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>{' '}
                        <Badge className={cn("border text-xs ml-1", getStatusConfig(infraStatus?.status)?.color)}>
                          {getStatusConfig(infraStatus?.status)?.label}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-gray-500">Type:</span>{' '}
                        <span className="font-medium">{infraStatus?.is_blocking !== false ? 'Blocking' : 'Non-Blocking'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Assignee:</span>{' '}
                        <span className="font-medium">{infraStatus?.task?.assignee_name || 'All Developers'}</span>
                      </div>
                      {infraStatus?.task?.description && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Description:</span>{' '}
                          <span>{infraStatus.task.description}</span>
                        </div>
                      )}
                      {infraStatus?.started_at && (
                        <div>
                          <span className="text-gray-500">Started:</span>{' '}
                          <span>{formatDistanceToNow(new Date(infraStatus.started_at), { addSuffix: true })}</span>
                        </div>
                      )}
                      {infraStatus?.completed_at && (
                        <div>
                          <span className="text-gray-500">Completed:</span>{' '}
                          <span>{formatDistanceToNow(new Date(infraStatus.completed_at), { addSuffix: true })}</span>
                        </div>
                      )}
                    </div>

                    {/* Status Change Controls */}
                    {isOwner && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label htmlFor="assignee-select" className="text-xs">Change Assignee</Label>
                            <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                              <SelectTrigger id="assignee-select" className="h-9">
                                <SelectValue placeholder="Select assignee" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Developers</SelectItem>
                                {keyHolders.map((holder) => (
                                  <SelectItem key={holder.user_id} value={holder.user_id}>
                                    {holder.name || holder.email}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {selectedAssignee !== infraStatus?.task?.assignee_id && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="w-full mt-2"
                                onClick={handleAssigneeChange}
                                disabled={changingStatus}
                              >
                                {changingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Assignee'}
                              </Button>
                            )}
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Move Build</Label>
                            <div className="flex gap-2">
                              {infraStatus?.status === 'in_progress' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleStatusChange('not_started')}
                                    disabled={changingStatus}
                                    className="flex-1"
                                  >
                                    ← To Do
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleStatusChange('complete')}
                                    disabled={changingStatus}
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                  >
                                    Done ✓
                                  </Button>
                                </>
                              )}
                              {infraStatus?.status === 'complete' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleDismiss}
                                  disabled={dismissing}
                                  className="flex-1"
                                >
                                  {dismissing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
                                  Dismiss from Board
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Configuration Summary - ALL Settings */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Project DNA */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Settings2 className="w-4 h-4 text-purple-500" />
                        Project DNA
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <div>
                          <span className="text-gray-500">Tenancy:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.project_dna?.tenancy_mode)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Compliance:</span>{' '}
                          <span className="font-medium">
                            {buildConfig?.project_dna?.compliance?.length > 0 
                              ? formatValue(buildConfig.project_dna.compliance) 
                              : 'None'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Delivery:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.project_dna?.delivery_lifecycle)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Security:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.project_dna?.security_model)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Architecture:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.project_dna?.architecture_style)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Quality:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.project_dna?.quality_tier)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Core Infrastructure */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Server className="w-4 h-4 text-blue-500" />
                        Core Infrastructure
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <div>
                          <span className="text-gray-500">Cloud:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.cloud_provider)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Region:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.region)}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500">Compute:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.compute)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Services */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Globe className="w-4 h-4 text-green-500" />
                        Services
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <div>
                          <span className="text-gray-500">Database:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.services?.database)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Storage:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.services?.storage) || 'None'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Cache:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.services?.cache) || 'None'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Queue:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.services?.queue) || 'None'}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500">Auth:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.services?.auth)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Networking */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Globe className="w-4 h-4 text-cyan-500" />
                        Networking
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <div>
                          <span className="text-gray-500">Ingress:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.networking?.ingress)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">API Structure:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.networking?.api_structure)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Integrations */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Settings2 className="w-4 h-4 text-indigo-500" />
                        Integrations
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <div>
                          <span className="text-gray-500">Observability:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.integrations?.observability)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">APM:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.integrations?.apm) || 'None'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Feature Flags:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.integrations?.feature_flags) || 'None'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Error Tracking:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.integrations?.error_tracking) || 'None'}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Workflow & Secrets */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileCode className="w-4 h-4 text-indigo-500" />
                        Workflow & Secrets
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <div>
                          <span className="text-gray-500">Branching:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.workflow?.branching)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">PR Approvals:</span>{' '}
                          <span className="font-medium">{buildConfig?.environment?.workflow?.pr_approvals ?? 'Not set'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Secret Store:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.secrets?.store)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Naming:</span>{' '}
                          <span className="font-medium">{formatValue(buildConfig?.environment?.secrets?.naming)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Custom Rules */}
                {buildConfig?.constraint_templates?.custom_rules?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileCode className="w-4 h-4 text-amber-500" />
                        Custom Rules
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm">
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        {buildConfig.constraint_templates.custom_rules.map((rule, index) => (
                          <li key={index}>{rule}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
          
          {/* Modal Footer */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button variant="outline" onClick={() => setShowSummaryModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Build Card Component
const BuildCard = ({ task, infraStatus, statusConfig, onClick }) => {
  const StatusIcon = statusConfig.icon;
  
  return (
    <Card 
      className="shadow-md hover:shadow-lg transition-all cursor-pointer border-2 hover:border-blue-300" 
      onClick={onClick}
      data-testid="active-build-card"
    >
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-1 flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 text-sm truncate">{task.title}</h4>
            <Badge className={cn("border text-xs", statusConfig.color)}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
          </div>
          {infraStatus.is_blocking !== false ? (
            <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs shrink-0 ml-2">
              <Lock className="w-3 h-3 mr-1" />
              Blocking
            </Badge>
          ) : (
            <Badge variant="outline" className="text-gray-500 text-xs shrink-0 ml-2">
              <Unlock className="w-3 h-3 mr-1" />
              Non-Blocking
            </Badge>
          )}
        </div>
        
        {/* Blocked indicator */}
        {infraStatus.is_blocked && (
          <Alert className="py-2 border-red-200 bg-red-50">
            <AlertTriangle className="h-3 w-3 text-red-600" />
            <AlertDescription className="text-xs text-red-700">
              {infraStatus.blocker_reason || 'Blocked'}
            </AlertDescription>
          </Alert>
        )}
        
        {/* Description */}
        {task.description && !infraStatus.is_blocked && (
          <p className="text-xs text-gray-500 line-clamp-2">{task.description}</p>
        )}
        
        {/* Meta */}
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <span>{task.assignee_name || 'All'}</span>
          </div>
          {infraStatus.started_at && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{formatDistanceToNow(new Date(infraStatus.started_at), { addSuffix: true })}</span>
            </div>
          )}
        </div>
        
        {/* Click hint */}
        <div className="pt-2 border-t text-center">
          <span className="text-xs text-blue-500 flex items-center justify-center gap-1">
            {infraStatus.status === 'not_started' || infraStatus.status === 'failed' ? (
              <>
                <Edit3 className="w-3 h-3" />
                Click to edit
              </>
            ) : (
              <>
                <Eye className="w-3 h-3" />
                Click to view details
              </>
            )}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default BuildActivityView;
